import Pattern from '@/assets/pattern.svg'
import { Thoughts } from '@/components/assistant/Thoughts'
import { ClampedContent } from '@/components/ClampedContent'
import { ItemUpdatedDate } from '@/components/CollapsibleCard'
import { useTrace } from '@/hooks'
import { Traceable } from '@repo/shared/dtos/assistant'
import { TimeType } from '@repo/shared/dtos/generic'
import { Card, CardContent, CardVariant } from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn } from '@repo/ui/utils'
import { isEmpty } from 'lodash'
import { RefreshCwIcon } from 'lucide-react'
import { ReactNode } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

export function SmartBlock({
  updatedAt,
  className,
  variant = 'secondary',
  hasPattern = false,
  onForceRefresh,
  ...props
}: (
  | {
      content: string
    }
  | { children: ReactNode }
) & {
  hasPattern?: boolean
  variant?: CardVariant
  className?: string
  updatedAt?: TimeType

  onForceRefresh?: () => void
} & HeaderProps) {
  const { tc } = useT()
  const handleForceRefresh = () => {
    if (props.isThinking) return
    onForceRefresh?.()
  }
  const core = (
    <>
      <Header
        {...props}
        rightSlot={
          updatedAt !== undefined && (
            <div className="flex items-center gap-1.5">
              <ItemUpdatedDate
                labelClassName="hidden"
                className="text-xs"
                updatedAt={updatedAt}
              />
              <TooltipWrapper
                content={tc('refresh', { cfl: true })}
                disabled={props.isThinking}
              >
                <div
                  onClick={handleForceRefresh}
                  className={cn(!props.isThinking && 'cursor-pointer')}
                >
                  <RefreshCwIcon
                    className={cn(
                      'text-primary-500 hover:text-primary size-3',
                      props.isThinking && 'animate-spin',
                    )}
                  />
                </div>
              </TooltipWrapper>
            </div>
          )
        }
      />

      {(('content' in props && isEmpty(props.content)) ||
        ('children' in props && !props.children)) &&
      props.isThinking ? (
        <Loader />
      ) : 'content' in props ? (
        <ClampedContent
          buttonProps={{
            variant: 'ghost',
            className: 'w-fit',
          }}
          content={props.content}
          lines={8}
        />
      ) : (
        <>{props.children}</>
      )}
    </>
  )
  if (hasPattern)
    return (
      <div
        className={cn(
          'bg-primary-900 relative flex w-full flex-col overflow-hidden rounded-lg p-4 xl:p-6',
          className,
        )}
      >
        <div
          className="absolute inset-0 bottom-0 left-0 right-0 top-0 -z-0 opacity-10"
          style={{
            backgroundImage: `url(${Pattern})`,
            backgroundPosition: `${Math.random() * 100}% ${Math.random() * 100}%`,
          }}
        />

        <div className="text-primary-200 z-0 flex flex-col gap-2">{core}</div>
      </div>
    )
  else
    return (
      <Card
        variant={variant}
        className={cn(
          'w-full',
          variant === 'default' && 'bg-opacity-20',
          className,
        )}
      >
        <CardContent className="text-primary flex w-full flex-col gap-2">
          {core}
        </CardContent>
      </Card>
    )
}

type HeaderProps = {
  isThinking: boolean
  rightSlot?: ReactNode
  thinkingTransKeys?: string[]
  title: string | ReactNode
  headerClassName?: string
} & Traceable

function Header({
  isThinking,
  rightSlot,
  thinkingTransKeys = ['thinking'],
  title,
  traceUrl,
  headerClassName,
}: HeaderProps) {
  const { isEN } = useT()
  const { Trace } = useTrace({ traceUrl })

  return (
    <div className="flex items-center justify-between">
      <div
        className={cn(
          'flex items-center gap-1',
          isThinking ? 'text-primary-400' : 'text-primary',
          headerClassName,
        )}
      >
        <div className="text-primary-400 -rotate-12">
          <IoSparklesSharp
            className={cn('mr-px size-5', isThinking && 'animate-pulse')}
          />
        </div>
        {isThinking ? (
          <>
            <span>
              {isThinking ? (isEN ? 'CaseAI:' : 'CaseAI :') : 'CaseAI'}
            </span>
            <span className="lowercase">
              <Thoughts
                isThinking={isThinking}
                loadingTransKeys={thinkingTransKeys}
              />
            </span>
          </>
        ) : (
          <div className="font-medium">{title}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {rightSlot}
        <Trace />
      </div>
    </div>
  )
}

export function Loader() {
  return (
    <div className="mt-2 flex w-full flex-col gap-2">
      <div className="bg-primary-300/50 h-5 animate-pulse rounded-md" />
      <div className="bg-primary-300/50 h-5 animate-pulse rounded-md" />
      <div className="bg-primary-300/50 h-5 w-10/12 animate-pulse rounded-md" />
    </div>
  )
}
